import React from "react";
import PropTypes from "prop-types";
import R from "ramda";
import { CellMeasurer } from "react-virtualized";
import FaAngleUp from "react-icons/fa/angle-up";
import { gridStyleType } from "custom-prop-types";
import styles from "components/grid/grid-components.scss";
import { CategoryCellFormatter } from "components/grid/cell-formatters/category-cell-formatter";
import { PaymentTypeCellFormatter } from "components/grid/cell-formatters/payment-type-cell-formatter";
import { ViewedCellFormatter } from "components/grid/cell-formatters/viewed-cell-formatter";
import { DownloadUrlCellFormatter } from "components/grid/cell-formatters/download-url-cell-formatter";
import { UploadSourceCellFormatter } from "components/grid/cell-formatters/upload-source-cell-formatter";
import { MoneyCellFormatter } from "components/grid/cell-formatters/money-cell-formatter";
import { DateCellFormatter, SimpleDateCellFormatter } from "components/grid/cell-formatters/date-cell-formatter";
import { SelectCellFormatter } from "components/grid/cell-formatters/select-cell-formatter";
import { VendorRuleCellFormatter } from "components/grid/cell-formatters/vendor-rule-cell-formatter";
import { CategoryRuleCellFormatter } from "components/grid/cell-formatters/category-rule-cell-formatter";
import { InputCellFormatter } from "components/grid/cell-formatters/input-cell-formatter";
import { TypeCellFormatter } from "components/grid/cell-formatters/type-cell-formatter";
import {
  EnvelopeUploadedCellFormatter,
  ReceivedCellFormatter,
  SentCellFormatter,
  StatusCellFormatter,
  EnvelopeCellFormatter,
  BillableCellFormatter
} from "components/grid/cell-formatters/envelopes-grid-cell-formatters";

export const CellRenderer = ({ columnIndex, rowIndex, style, parent, newKey }) => {
  const rowClassName = getRowClassName(rowIndex);
  if (rowIndex === 0) {
    return cellRendererTop({ columnIndex, style, parent, rowIndex, newKey });
  }
  return cellRendererMain({ columnIndex, rowIndex, style, parent, rowClassName, newKey });
};

CellRenderer.propTypes = {
  columnIndex: PropTypes.number.isRequired,
  newKey: PropTypes.string.isRequired,
  parent: PropTypes.shape({ props: PropTypes.object }).isRequired,
  rowIndex: PropTypes.number.isRequired,
  style: gridStyleType.isRequired
};

const cellRendererTop = ({ columnIndex, style, parent, rowIndex, newKey }) => {
  const { cache, columns, orderDirection, orderBy, onOrderBy } = parent.props;
  const gridColumns = columns.filter(col => col.visible === true);
  const column = gridColumns[columnIndex];
  const sortableClassName = column.sortable ? styles.sortable : "";
  const isSortActive = column.key === orderBy;
  if (column.key === "select") {
    const { documents, selectedDocumentIds, onRowsDeselected, onRowsSelected } = parent.props;
    const ids = documents.map((document) => document.id);
    return (
      <CellMeasurer
        cache={ cache }
        columnIndex={ columnIndex }
        key={ newKey }
        parent={ parent }
        rowIndex={ rowIndex }
      >
        <div className={ styles["select-all"] } key={ newKey } style={ style }>
          <SelectCellFormatter
            id={ ids }
            isSelected={ selectedDocumentIds.length > 19 }
            onRowsDeselected={ onRowsDeselected }
            onRowsSelected={ onRowsSelected }
          />
        </div>
      </CellMeasurer>
    );
  }
  return (
    <CellMeasurer
      cache={ cache }
      columnIndex={ columnIndex }
      key={ newKey }
      parent={ parent }
      rowIndex={ rowIndex }
    >
      <div
        className={ `${styles.header} ${sortableClassName}` }
        key={ newKey }
        onClick={ () => sortable(onOrderBy, column) }
        style={ style }
      >
        <span className={ styles["header-content"] }>
          {column.name}
          {sortIndicator(orderDirection, isSortActive)}
        </span>
      </div>
    </CellMeasurer>
  );
};

const sortable = (onOrderBy, column) => {
  if (!column.sortable) {
    return null;
  }
  return onOrderBy(column.key);
};

const sortIndicator = (orderDirection, isSortActive) => {
  const className = orderDirection === "ASC" ? "up" : "down";
  if (isSortActive) {
    return <FaAngleUp className={ styles[className] } size={ 15 } />;
  }
  return null;
};

cellRendererTop.propTypes = {
  columnIndex: PropTypes.number.isRequired,
  newKey: PropTypes.string.isRequired,
  parent: PropTypes.shape({ props: PropTypes.object }).isRequired,
  rowIndex: PropTypes.number.isRequired,
  style: gridStyleType.isRequired
};


const cellRendererMain = ({ columnIndex, rowIndex, style, parent, rowClassName, newKey }) => {
  const { focusParam, documents, isRowFocusable, columns, onFocusRow } = parent.props;
  const newDocuments = R.prepend({}, documents);
  const gridColumns = columns.filter(col => col.visible === true);
  const row = newDocuments[rowIndex];
  const cellKey = gridColumns[columnIndex].key;
  const content = row[cellKey];
  const handleRowClick = isRowFocusable && cellKey !== "select" ? () => onFocusRow(row, style, focusParam) : null;
  return cellFormatterContainer(
    columnIndex,
    cellKey,
    newKey,
    style,
    content,
    handleRowClick,
    row,
    rowIndex,
    rowClassName,
    parent
  );
};

cellRendererMain.propTypes = {
  columnIndex: PropTypes.number.isRequired,
  newKey: PropTypes.string.isRequired,
  parent: PropTypes.shape({ props: PropTypes.object }).isRequired,
  rowClassName: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  style: gridStyleType.isRequired
};

const cellFormatterContainer = (
  columnIndex,
  cellKey,
  key,
  style,
  content,
  handleRowClick,
  row,
  rowIndex,
  rowClassName,
  parent
) => {
  const isFocused = parent.props.focusedDocument && row.id === parent.props.focusedDocument.id;
  const viewedStyle = row.viewed === false ? styles.unviewed : "";
  const focusedStyle = isFocused ? styles.focused : "";
  const overflowStyle = cellKey === "type" || "source" ? styles.overflow : "";
  const changingClasses = `${viewedStyle} ${focusedStyle} ${overflowStyle}`;
  return (
    <CellMeasurer
      cache={ parent.props.cache }
      columnIndex={ columnIndex }
      key={ key }
      parent={ parent }
      rowIndex={ rowIndex }
    >
      <div
        className={ `${styles["main-cells"]} ${rowClassName} ${changingClasses}` }
        key={ key }
        onClick={ handleRowClick }
        style={ style }
      >
        {cellFormatters(cellKey, key, style, content, parent, row, rowIndex, isFocused)}
      </div>
    </CellMeasurer>
  );
};

const cellFormatters = (cellKey, key, style, content, parent, row, rowIndex, isFocused) => {
  switch (cellKey) {
    case "select":
      return (
        <SelectCellFormatter
          documents={ parent.props.documents }
          id={ row.id }
          isSelected={ parent.props.selectedDocumentIds.includes(row.id) }
          isShiftPressed={ parent.props.isShiftPressed }
          onRowsDeselected={ parent.props.onRowsDeselected }
          onRowsSelected={ parent.props.onRowsSelected }
          selectedDocumentIds={ parent.props.selectedDocumentIds }
        />
      );
    case "issued":
      return (
        <DateCellFormatter
          accountId={ parent.props.accountId }
          document={ row }
          isFocused={ isFocused }
          onUpdateInlineDocument={ parent.props.onUpdateInlineDocument }
          value={ content }
        />
      );
    case "type":
      return <TypeCellFormatter value={ content } />;
    case "uploaded":
    case "exported":
      return <SimpleDateCellFormatter value={ content } />;
    case "total":
      return (
        <MoneyCellFormatter
          accountId={ parent.props.accountId }
          currency={ parent.props.currency }
          defaultCurrency={ parent.props.preferredCurrency }
          document={ row }
          field={ "total" }
          isFocused={ isFocused }
          onUpdateDocument={ parent.props.onUpdateDocument }
          onUpdateInlineDocument={ parent.props.onUpdateInlineDocument }
          value={ content }
        />
      );
    case "tax":
      return (
        <MoneyCellFormatter
          accountId={ parent.props.accountId }
          currency={ parent.props.currency }
          defaultCurrency={ parent.props.preferredCurrency }
          document={ row }
          field={ "tax" }
          isFocused={ isFocused }
          onUpdateDocument={ parent.props.onUpdateDocument }
          onUpdateInlineDocument={ parent.props.onUpdateInlineDocument }
          value={ content }
        />
      );
    case "categories":
      return (
        <CategoryCellFormatter
          documentCount={ parent.props.documents.length }
          rowIndex={ rowIndex }
          value={ content }
        />
      );
    case "rules":
      return renderCorrectCellFormatter(content, parent.props, row);
    case "paymentType":
      return (
        <PaymentTypeCellFormatter
          documentCount={ parent.props.documents.length }
          rowIndex={ rowIndex }
          value={ content }
        />
      );
    case "viewed":
      return <ViewedCellFormatter value={ content } />;
    case "downloadUrl":
    case "url":
      return <DownloadUrlCellFormatter value={ row.attachment || content } />;
    case "source":
      return <UploadSourceCellFormatter value={ content } />;
    case "received":
      return <ReceivedCellFormatter value={ content } />;
    case "sent":
      return <SentCellFormatter value={ content } />;
    case "status":
      return <StatusCellFormatter value={ content } />;
    case "envelope":
      return <EnvelopeCellFormatter value={ content } />;
    case "envelopeUploaded":
      return <EnvelopeUploadedCellFormatter value={ content } />;
    case "code":
      return (
        <EnvelopeCellFormatter
          isLinkable={ parent.props.isLinkable }
          onAddEnvelope={ parent.props.onAddEnvelope }
          onSetFlashMessage={ parent.props.onSetFlashMessage }
          slug={ parent.props.slug }
          value={ content }
        />
      );
    case "billable":
      return <BillableCellFormatter value={ content } />;
    case "vendor":
      return (
        <InputCellFormatter
          accountId={ parent.props.accountId }
          document={ row }
          documentCount={ parent.props.documents.length }
          field={ "vendor" }
          isFocused={ isFocused }
          onUpdateDocument={ parent.props.onUpdateDocument }
          onUpdateInlineDocument={ parent.props.onUpdateInlineDocument }
          rowIndex={ rowIndex }
          value={ content || row.name }
        />
      );
    case "invoiceNumber":
      return (
        <InputCellFormatter
          accountId={ parent.props.accountId }
          document={ row }
          documentCount={ parent.props.documents.length }
          field={ "invoiceNumber" }
          isFocused={ isFocused }
          onUpdateDocument={ parent.props.onUpdateDocument }
          onUpdateInlineDocument={ parent.props.onUpdateInlineDocument }
          rowIndex={ rowIndex }
          value={ content }
        />
      );
    default:
      return content;
  }
};

const getRowClassName = (row) => {
  if (row % 2 === 0) {
    return styles["even-row"];
  }
  return styles["odd-row"];
};

export const getColumnWidth = (index, width, visibleColumns) => {
  if (visibleColumns[index].fixedWidth) {
    return visibleColumns[index].fixedWidth;
  }
  const { fixedColumns, fixedWidth } = addFixedWidths(visibleColumns);
  const newWidth = width - fixedWidth;
  const extraRowWidth = visibleColumns.reduce((previous, current) => ({ width: previous.width + current.width }));
  const extraColumnWidth = (1 - extraRowWidth.width) / (visibleColumns.length - fixedColumns);
  const columnWidth = visibleColumns[index].width + extraColumnWidth;
  return newWidth * columnWidth;
};

const addFixedWidths = (visibleColumns) => {
  let fixedWidth = 0;
  let fixedColumns = 0;
  visibleColumns.forEach((column) => {
    if (column.fixedWidth) {
      fixedColumns += 1;
      fixedWidth += column.fixedWidth;
    }
  });
  return { fixedColumns, fixedWidth };
};

export const getGridHeight = (limit, totalResults, rowHeight) => {
  const rows = R.min(limit, totalResults);
  if (limit === -1) {
    return (totalResults * rowHeight) + 50;
  }
  if (rows === 0) {
    return 650;
  }
  return (rows * rowHeight) + 50;
};

const renderCorrectCellFormatter = (content, props, row) => {
  if (props.isManageVendorsPage) {
    return (
      <VendorRuleCellFormatter
        accountId={ props.accountId }
        onDeleteIncludeRule={ props.onDeleteIncludeRule }
        value={ content }
        vendorName={ row.name }
      />
    );
  }
  return (
    <CategoryRuleCellFormatter
      accountId={ props.accountId }
      id={ row.id }
      removeIncludeRule={ props.onRemoveIncludeRule }
      value={ content }
    />
  );
};

renderCorrectCellFormatter.propTypes = {
  accountId: PropTypes.string.isRequired,
  isManageVendorsPage: PropTypes.bool.isRequired,
  onDeleteIncludeRule: PropTypes.func.isRequired,
  onRemoveIncludeRule: PropTypes.func.isRequired
};



// WEBPACK FOOTER //
// ./src/js/components/grid/grid-components.jsx